import { debugLog } from './debugService';

/**
 * Tab tracking via a UUID-set model.
 *
 * Instead of maintaining a shared counter that must be incremented and
 * decremented (and can drift when unload events are missed), each browser
 * tab registers a unique UUID under its courseId. The "count" is simply the
 * number of live UUIDs for that course.
 *
 * Each UUID carries a `heartbeatAt` timestamp that is refreshed periodically.
 * Stale entries are pruned at write boundaries (register, unregister, touch)
 * rather than on reads, so getter functions remain pure and side-effect-free.
 * Because the heartbeat runs every 60 s, pruning happens at least that often
 * in any tab that is alive.
 *
 * Because each tab only ever writes its own UUID key, the risk of a cross-tab
 * race corrupting the data is minimal — the worst case is that one tab's
 * heartbeat update is lost, and it gets refreshed again a minute later.
 *
 * localStorage schema (key: "editorTabs"):
 *
 *   {
 *     "<courseId>": {
 *       "<uuid-a>": { "heartbeatAt": "2026-07-10T12:00:00.000Z" },
 *       "<uuid-b>": { "heartbeatAt": "2026-07-10T12:00:05.000Z" }
 *     }
 *   }
 */

const STORAGE_KEY = 'editorTabs';

/**
 * A tab entry whose heartbeat is older than this is considered dead.
 *
 * The heartbeat interval is 60 s, so 3 minutes (3× the interval) gives a
 * comfortable margin for brief browser freezes while still detecting
 * crashed or discarded tabs within a few minutes.  When a laptop sleeps
 * for hours, the heartbeat timer is frozen too — on wake, the surviving
 * tab resumes its heartbeat immediately, keeping itself alive, while
 * entries from tabs that died during sleep are pruned on the next write.
 */
const TAB_MAX_AGE_MS = 3 * 60 * 1000;

interface TabEntry {
	heartbeatAt: string;
}

type CourseTabSet = { [uuid: string]: TabEntry };
type TabStore = { [courseId: string]: CourseTabSet };

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/** Read the raw tab store, returning {} on any failure. */
const readStore = (): TabStore => {
	try {
		const raw = localStorage.getItem(STORAGE_KEY);
		if (raw === null) return {};

		const parsed = JSON.parse(raw);
		if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
			debugLog('localStorage', 'editorTabs is not an object, resetting', parsed);
			localStorage.removeItem(STORAGE_KEY);
			return {};
		}
		return parsed as TabStore;
	} catch (e) {
		debugLog('localStorage', 'Failed to parse editorTabs, resetting', e);
		try { localStorage.removeItem(STORAGE_KEY); } catch { /* noop */ }
		return {};
	}
};

/** Write the tab store back to localStorage. */
const writeStore = (store: TabStore): void => {
	try {
		localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
	} catch (e) {
		debugLog('localStorage', 'Failed to write editorTabs', e);
	}
};

/**
 * Return only the live entries from a course's tab set, filtering out
 * any whose heartbeat is older than TAB_MAX_AGE_MS.
 */
const getLiveCourseTabs = (set: CourseTabSet, courseId: string): CourseTabSet => {
	const now = Date.now();
	const live: CourseTabSet = {};

	for (const [uuid, entry] of Object.entries(set)) {
		if (!entry || typeof entry !== 'object' || !entry.heartbeatAt) continue;

		const age = now - new Date(entry.heartbeatAt).getTime();
		if (age <= TAB_MAX_AGE_MS) {
			live[uuid] = entry;
		} else {
			debugLog(
				'tabs',
				`Pruning dead tab ${uuid} for course ${courseId} (last heartbeat ${Math.round(age / 1000 / 60)}min ago)`
			);
		}
	}

	return live;
};

/**
 * Read the store, prune dead entries from every course, and write back
 * if anything changed. Returns the pruned store.
 *
 * Called by the three write functions (register, unregister, touch) so
 * that pruning piggybacks on mutations that are already writing to
 * localStorage.  Read-only functions never call this.
 */
const readAndPruneStore = (): TabStore => {
	const store = readStore();
	let changed = false;

	for (const courseId of Object.keys(store)) {
		const set = store[courseId];
		if (typeof set !== 'object' || set === null) continue;

		const live = getLiveCourseTabs(set, courseId);
		const liveCount = Object.keys(live).length;

		if (liveCount !== Object.keys(set).length) {
			changed = true;
			if (liveCount > 0) {
				store[courseId] = live;
			} else {
				delete store[courseId];
			}
		}
	}

	if (changed) writeStore(store);

	return store;
};

// ---------------------------------------------------------------------------
// Migrate legacy "tabsOpened" data (one-time, best-effort)
// ---------------------------------------------------------------------------
// The old format stored { "<courseId>": <count> | { count, updatedAt } }
// under the key "tabsOpened". If it exists we can't meaningfully recover
// individual tab UUIDs, but we can clear it so getTabsOpened / getCount
// don't see stale phantom counts.
(() => {
	try {
		const legacy = localStorage.getItem('tabsOpened');
		if (legacy !== null) {
			debugLog('tabs', 'Removing legacy tabsOpened entry from localStorage');
			localStorage.removeItem('tabsOpened');
		}
	} catch { /* noop */ }
})();

// ---------------------------------------------------------------------------
// Public API — reads (pure, no side-effects)
// ---------------------------------------------------------------------------

/**
 * Get the tab counts for every course that has at least one live tab.
 *
 * This is a pure read — it filters stale entries in memory but does not
 * write back to localStorage.  Pruning happens at write boundaries
 * (registerTab, unregisterTab, touchTab).
 *
 * Returns `{ [courseId]: liveTabCount }`.
 */
export const getTabsOpened = (): { [courseId: string]: number } => {
	const store = readStore();
	const counts: { [courseId: string]: number } = {};

	for (const [courseId, set] of Object.entries(store)) {
		if (typeof set !== 'object' || set === null) continue;

		const liveCount = Object.keys(getLiveCourseTabs(set, courseId)).length;
		if (liveCount > 0) {
			counts[courseId] = liveCount;
		}
	}

	return counts;
};

/** Get the number of live tabs for a specific course. */
export const getCourseTabsOpened = (courseId: number): number => {
	const counts = getTabsOpened();
	return counts[courseId] || 0;
};

// ---------------------------------------------------------------------------
// Public API — writes (prune stale entries as a side-effect)
// ---------------------------------------------------------------------------

/**
 * Register this tab for a course. Returns a UUID that the caller must
 * store and pass to `unregisterTab` / `touchTab`.
 */
export const registerTab = (courseId: number | string): string => {
	const uuid = crypto.randomUUID();
	const key = String(courseId);

	const store = readAndPruneStore();
	if (!store[key] || typeof store[key] !== 'object') {
		store[key] = {};
	}
	store[key][uuid] = { heartbeatAt: new Date().toISOString() };
	writeStore(store);

	return uuid;
};

/**
 * Unregister a tab. Called on unload / cleanup.
 */
export const unregisterTab = (courseId: number | string, uuid: string): void => {
	const key = String(courseId);
	const store = readAndPruneStore();
	const set = store[key];

	if (set && typeof set === 'object') {
		delete set[uuid];
		if (Object.keys(set).length === 0) {
			delete store[key];
		}
	}

	writeStore(store);
};

/**
 * Refresh this tab's heartbeat timestamp so it is not pruned.
 * Called periodically (e.g. every 60 s).
 */
export const touchTab = (courseId: number | string, uuid: string): void => {
	try {
		const key = String(courseId);
		const store = readAndPruneStore();
		const set = store[key];

		if (set && typeof set === 'object' && set[uuid]) {
			set[uuid].heartbeatAt = new Date().toISOString();
			writeStore(store);
		}
	} catch {
		// Best-effort — silent failure is fine for a heartbeat
	}
};
